import { hideSlots } from '../../utils/storeSafe';
import React, { useState, useEffect } from 'react';
import { VStack, Box, Icon, Tooltip, Text, HStack, Badge } from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Home,
  Gamepad2,
  Wallet,
  Trophy,
  User,
  ShoppingCart,
  GraduationCap,
  Settings,
  Menu,
  X,
  Search,
  MessageCircle,
  Users,
  Dices,
} from 'lucide-react';
import { POKER_COLORS } from '../../theme/designSystem';
import { useAuth } from '../../contexts/AuthContext';
import { subscribeToActiveUsersCount } from '../../services/presenceService';
import { subscribeToUnreadCount } from '../../services/messageService';
import { subscribeToPendingInvites } from '../../services/inviteService';
import { db } from '../../services/firebase';
import { doc, getDoc } from 'firebase/firestore';

interface NavigationProps {
  onOpenUserSearch?: () => void;
}

const Navigation: React.FC<NavigationProps> = ({ onOpenUserSearch }) => {
  const location = useLocation();
  const { userProfile, currentUser } = useAuth();
  const [activeUsersCount, setActiveUsersCount] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [unreadDMs, setUnreadDMs] = useState(0);
  const [pendingInvites, setPendingInvites] = useState(0);
  const [isAgentVerified, setIsAgentVerified] = useState(false);
  
  // Check if user is admin (by profile flag OR by known admin UID)
  const ADMIN_UID = '11tBjYey4AgwlZKbVg0SaqLXhtp2';
  const isAdmin = userProfile?.isAdmin === true || userProfile?.role === 'admin' || currentUser?.uid === ADMIN_UID;
  
  //  Check if user is an agent (from profile OR agents collection)

  const isAgent = userProfile?.isAgent === true || isAgentVerified;

  //  Check agents collection directly as fallback

  useEffect(() => {
    const checkAgentStatus = async () => {
      if (!currentUser?.uid) {
        setIsAgentVerified(false);
        return;
      }
      
      // If already marked as agent in profile, no need to check
      if (userProfile?.isAgent === true) {
        setIsAgentVerified(true);
        return;
      }
      
      // Check agents collection directly
      try {
        const agentDoc = await getDoc(doc(db, 'agents', currentUser.uid));
        if (agentDoc.exists() && agentDoc.data().active === true) {
          setIsAgentVerified(true);
        } else {
          setIsAgentVerified(false);
        }
      } catch (error) {
        console.error('Error checking agent status:', error);
        setIsAgentVerified(false);
      }
    };
    
    checkAgentStatus();
  }, [currentUser?.uid, userProfile?.isAgent]);
  
  // Total notification count for Messages badge
  const notificationCount = unreadDMs + pendingInvites;

  // Subscribe to active users count
  useEffect(() => {
    const unsubscribe = subscribeToActiveUsersCount((count) => {
      setActiveUsersCount(count);
    });
    return () => unsubscribe();
  }, []);

  // Subscribe to unread DMs count
  useEffect(() => {
    if (!currentUser?.uid) return;
    const unsubscribe = subscribeToUnreadCount(currentUser.uid, (count) => {
      setUnreadDMs(count);
    });
    return () => unsubscribe();
  }, [currentUser?.uid]);

  // Subscribe to pending invites count
  useEffect(() => {
    if (!currentUser?.uid) return;
    const unsubscribe = subscribeToPendingInvites(currentUser.uid, (invites) => {
      setPendingInvites(invites.length);
    });
    return () => unsubscribe();
  }, [currentUser?.uid]);

  //  Build nav items dynamically

  const navItems: { icon: any; label: string; to: string; highlight?: boolean }[] = [
    { icon: Home, label: 'Home', to: '/' },
    { icon: Gamepad2, label: 'Tables', to: '/tables' },
    { icon: GraduationCap, label: 'Training', to: '/training', highlight: true },
    { icon: Trophy, label: 'Tournaments', to: '/tournaments' },
    { icon: ShoppingCart, label: 'Buy Coins', to: '/buy-coins' },
    { icon: Wallet, label: 'Wallet', to: '/wallet' },
    ...(hideSlots() ? [] : [{ icon: Dices, label: 'More Games', to: '/slots' }]),
    { icon: MessageCircle, label: 'Messages', to: '/messages' },
    { icon: User, label: 'Profile', to: '/profile' },
  ];

  //  Add agent route if user is an agent (before admin)

  if (isAgent) {
    navItems.push({ icon: Users, label: 'Agent', to: '/agent' });
  }

  // Add admin route if user is admin
  if (isAdmin) {
    navItems.push({ icon: Settings, label: 'Admin', to: '/admin' });
  }

  const isActive = (path: string) => {
    if (path === '/training') {
      return location.pathname === '/training' || location.pathname.startsWith('/training/');
    }
    if (path === '/slots') {
      return location.pathname === '/slots' || location.pathname.startsWith('/slots/');
    }
    if (path === '/messages') {
      return location.pathname === '/messages';
    }
    if (path === '/agent') {
      return location.pathname === '/agent';
    }
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname === path;
  };

  return (
    <Box
      as="nav"
      position="fixed"
      left={0}
      top={0}
      bottom={0}
      w={isExpanded ? '200px' : '80px'}
      bg={POKER_COLORS.darkBg}
      borderRight={`1px solid ${POKER_COLORS.cyan}33`}
      py={4}
      px={isExpanded ? 3 : 0}
      display={{ base: 'none', md: 'flex' }}
      flexDirection="column"
      alignItems={isExpanded ? 'stretch' : 'center'}
      zIndex={100}
      transition="all 0.3s ease"
      overflowX="hidden"
    >
      {/* Toggle Button */}
      <Box
        as="button"
        onClick={() => setIsExpanded(!isExpanded)}
        p={2}
        mb={3}
        borderRadius="lg"
        bg="rgba(255, 255, 255, 0.05)"
        color="white"
        cursor="pointer"
        transition="all 0.2s"
        display="flex"
        alignItems="center"
        justifyContent="center"
        alignSelf={isExpanded ? 'flex-end' : 'center'}
        _hover={{
          bg: 'rgba(255, 255, 255, 0.1)',
        }}
      >
        <Icon as={isExpanded ? X : Menu} w={5} h={5} />
      </Box>

      {/* Logo */}
      <Box mb={3} p={2} textAlign="center">
        <Text fontSize="2xl" fontWeight="bold" color={POKER_COLORS.cyan}>
          

        </Text>
        {isExpanded && (
          <Text fontSize="sm" fontWeight="bold" color={POKER_COLORS.cyan} mt={1}>
            PokerSweepStars
          </Text>
        )}
      </Box>

      {/* ACTIVE USERS COUNT */}
      <Tooltip 
        label={`${activeUsersCount} Players Online`} 
        placement="right" 
        isDisabled={isExpanded}
        bg="#1a1a2e" 
        color="#22c55e"
      >
        <Box
          mb={4}
          p={2}
          borderRadius="lg"
          bg="rgba(34, 197, 94, 0.15)"
          border="2px solid rgba(34, 197, 94, 0.5)"
          display="flex"
          flexDirection={isExpanded ? 'row' : 'column'}
          alignItems="center"
          justifyContent={isExpanded ? 'flex-start' : 'center'}
          gap={isExpanded ? 3 : 1}
          cursor="default"
          transition="all 0.2s"
          _hover={{
            bg: 'rgba(34, 197, 94, 0.25)',
            borderColor: 'rgba(34, 197, 94, 0.8)',
          }}
        >
          {/* Pulsing green dot */}
          <Box
            w="10px"
            h="10px"
            borderRadius="full"
            bg="#22c55e"
            boxShadow="0 0 8px rgba(34, 197, 94, 0.8)"
            flexShrink={0}
            animation="pulse 2s ease-in-out infinite"
            sx={{
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                '50%': { opacity: 0.6, transform: 'scale(0.9)' },
              },
            }}
          />
          {/* Count */}
          <Text
            fontSize={isExpanded ? 'sm' : 'md'}
            fontWeight="bold"
            color="#22c55e"
            lineHeight={1}
            whiteSpace="nowrap"
          >
            {isExpanded ? `${activeUsersCount} Online` : activeUsersCount}
          </Text>
        </Box>
      </Tooltip>

      {/* Nav Items */}
      <VStack spacing={1} flex={1} align="stretch">
        {navItems.map((item) => (
          <Tooltip 
            key={item.label} 
            label={item.label} 
            placement="right"
            isDisabled={isExpanded}
            bg={POKER_COLORS.darkTeal}
            color={POKER_COLORS.cyan}
          >
            <HStack
              as={RouterLink}
              to={item.to}
              p={3}
              borderRadius="xl"
              cursor="pointer"
              transition="all 0.2s"
              spacing={3}
              justify={isExpanded ? 'flex-start' : 'center'}
              bg={
                isActive(item.to) 
                  ? item.highlight 
                    ? `${POKER_COLORS.gold}22` 
                    : `${POKER_COLORS.cyan}22`
                  : 'transparent'
              }
              border="2px solid"
              borderColor={
                isActive(item.to) 
                  ? item.highlight 
                    ? POKER_COLORS.gold 
                    : POKER_COLORS.cyan 
                  : 'transparent'
              }
              _hover={{
                bg: item.highlight ? `${POKER_COLORS.gold}11` : `${POKER_COLORS.cyan}11`,
                transform: 'scale(1.02)',
              }}
            >
              {/* Icon with notification badge */}
              <Box position="relative">
                <Icon
                  as={item.icon}
                  w={5}
                  h={5}
                  flexShrink={0}
                  color={
                    isActive(item.to) 
                      ? item.highlight 
                        ? POKER_COLORS.gold 
                        : POKER_COLORS.cyan 
                      : 'gray.500'
                  }
                />
                {/* Notification badge for Messages */}
                {item.to === '/messages' && notificationCount > 0 && (
                  <Box
                    position="absolute"
                    top="-6px"
                    right="-6px"
                    bg="red.500"
                    color="white"
                    fontSize="2xs"
                    fontWeight="bold"
                    borderRadius="full"
                    minW="16px"
                    h="16px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    px={1}
                  >
                    {notificationCount > 99 ? '99+' : notificationCount}
                  </Box>
                )}
              </Box>
              {isExpanded && (
                <HStack spacing={2} flex={1}>
                  <Text
                    fontSize="sm"
                    fontWeight={isActive(item.to) ? 'bold' : 'medium'}
                    color={
                      isActive(item.to) 
                        ? item.highlight 
                          ? POKER_COLORS.gold 
                          : POKER_COLORS.cyan 
                        : 'gray.400'
                    }
                    whiteSpace="nowrap"
                  >
                    {item.label}
                  </Text>
                  {/* Notification badge in expanded mode */}
                  {item.to === '/messages' && notificationCount > 0 && (
                    <Badge
                      bg="red.500"
                      color="white"
                      fontSize="2xs"
                      borderRadius="full"
                      px={2}
                    >
                      {notificationCount > 99 ? '99+' : notificationCount}
                    </Badge>
                  )}
                </HStack>
              )}
            </HStack>
          </Tooltip>
        ))}

        {/* Find Players Button */}
        {onOpenUserSearch && (
          <Tooltip 
            label="Find Players" 
            placement="right"
            isDisabled={isExpanded}
            bg={POKER_COLORS.darkTeal}
            color={POKER_COLORS.cyan}
          >
            <HStack
              as="button"
              onClick={onOpenUserSearch}
              p={3}
              borderRadius="xl"
              cursor="pointer"
              transition="all 0.2s"
              spacing={3}
              justify={isExpanded ? 'flex-start' : 'center'}
              bg="transparent"
              border="2px solid transparent"
              _hover={{
                bg: `${POKER_COLORS.cyan}11`,
                transform: 'scale(1.02)',
              }}
            >
              <Icon as={Search} w={5} h={5} color="gray.500" flexShrink={0} />
              {isExpanded && (
                <Text fontSize="sm" fontWeight="medium" color="gray.400" whiteSpace="nowrap">
                  Find Players
                </Text>
              )}
            </HStack>
          </Tooltip>
        )}
      </VStack>

      {/* Bottom spacer */}
      <Box h={4} />
    </Box>
  );
};

export default Navigation;